import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Activity, AlertTriangle, Check, Copy } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { useTranslation } from "@/lib/i18n";
import { Integration, IntegrationType, useAddIntegration } from "@/lib/integrations-store";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const integrationSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  provider: z.string().min(1, "Provider is required"),
  type: z.enum(["LLM", "CRM", "Productivity", "Finance", "Custom"]),
  apiKey: z.string().min(8, "API Key must be at least 8 characters").optional().or(z.literal("")),
});

type IntegrationFormValues = z.infer<typeof integrationSchema>;

interface IntegrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PROVIDERS = ["OpenAI", "Anthropic", "Google", "HubSpot", "Stripe", "Slack", "Salesforce", "Zendesk", "Twilio", "Custom"];

const ANALYTICS_DATA = [
  { name: "Jan", usage: 4000, cost: 20 },
  { name: "Feb", usage: 7200, cost: 35 },
  { name: "Mar", usage: 12000, cost: 50 },
  { name: "Apr", usage: 15402, cost: 65 },
];

const createMockKey = (provider: string) =>
  `cf-${provider.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${crypto.randomUUID().slice(0, 12)}`;

export function IntegrationModal({ open, onOpenChange }: IntegrationModalProps) {
  const { t } = useTranslation();
  const addIntegration = useAddIntegration();
  const [testing, setTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null);

  const form = useForm<IntegrationFormValues>({
    resolver: zodResolver(integrationSchema),
    defaultValues: {
      name: "",
      provider: "OpenAI",
      type: "LLM",
      apiKey: "",
    },
  });

  const handleTestConnection = async () => {
    setTesting(true);
    setTestSuccess(null);

    const { provider, apiKey } = form.getValues();
    const success = provider.trim().length > 0 && apiKey.trim().length >= 8;

    setTestSuccess(success);
    toast[success ? "success" : "error"](success ? t("test_succeeded") : "Vui lòng nhập API key hợp lệ trước khi kiểm tra.");
    setTesting(false);
  };

  const onSubmit = async (data: IntegrationFormValues) => {
    try {
      const newIntegration: Integration = {
        id: `int_${crypto.randomUUID()}`,
        name: data.name,
        provider: data.provider,
        type: data.type as IntegrationType,
        status: "connected",
        metrics: {
          calls: 0,
          cost: 0,
          lastUsed: "Never",
          successRate: 100,
        },
        authData: {
          apiKey: data.apiKey || createMockKey(data.provider),
        },
      };

      addIntegration(newIntegration);
      toast.success("Đã tạo kết nối mới.");
      onOpenChange(false);
      form.reset();
      setTestSuccess(null);
    } catch {
      toast.error("Không thể lưu kết nối.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] max-w-3xl flex-col overflow-hidden p-0" aria-describedby="integration-desc">
        <DialogHeader className="border-b p-6">
          <DialogTitle id="integration-title">{t("create_integration")}</DialogTitle>
          <p id="integration-desc" className="text-sm text-muted-foreground">
            Cấu hình kết nối API với lớp bảo vệ cơ bản, kiểm tra nhanh và giám sát sử dụng ngay trong giao diện.
          </p>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <Tabs defaultValue="provider" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="h-12 w-full justify-start rounded-none border-b bg-transparent px-6">
              <TabsTrigger value="provider">Nhà cung cấp</TabsTrigger>
              <TabsTrigger value="auth">Xác thực</TabsTrigger>
              <TabsTrigger value="guardrails">Bảo vệ</TabsTrigger>
              <TabsTrigger value="analytics">Phân tích</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-6">
              <TabsContent value="provider" className="m-0 space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Tên kết nối</Label>
                      <Input id="name" placeholder="Ví dụ: OpenAI Production" {...form.register("name")} aria-label="Tên kết nối" />
                      {form.formState.errors.name ? (
                        <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="type">Loại kết nối</Label>
                      <select
                        id="type"
                        {...form.register("type")}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        aria-label="Loại kết nối"
                      >
                        <option value="LLM">LLM Provider</option>
                        <option value="CRM">CRM</option>
                        <option value="Productivity">Năng suất</option>
                        <option value="Finance">Tài chính</option>
                        <option value="Custom">API tùy chỉnh</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Chọn nhà cung cấp</Label>
                    <div className="grid grid-cols-4 gap-3">
                      {PROVIDERS.map((provider) => (
                        <div
                          key={provider}
                          onClick={() => form.setValue("provider", provider)}
                          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-3 transition-all ${
                            form.watch("provider") === provider
                              ? "border-primary bg-primary/10 ring-1 ring-primary"
                              : "hover:bg-accent"
                          }`}
                        >
                          <img
                            src={`https://logo.clearbit.com/${provider.toLowerCase()}.com`}
                            className="h-6 w-6 object-contain"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                            alt={provider}
                          />
                          <span className="text-[10px] font-medium">{provider}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="auth" className="m-0 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="api-key">API Key / Access Token</Label>
                  <div className="flex gap-2">
                    <Input
                      id="api-key"
                      type="password"
                      placeholder="sk-xxxxxxxxxxxxxxxx"
                      {...form.register("apiKey")}
                      aria-label="API Key"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      onClick={async () => {
                        const value = form.getValues("apiKey");
                        if (!value) {
                          toast.info("Chưa có API key để sao chép.");
                          return;
                        }

                        await navigator.clipboard.writeText(value);
                        toast.success("Đã sao chép API key.");
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing}
                    type="button"
                    variant="secondary"
                    className="w-full"
                    aria-label="Kiểm tra kết nối"
                  >
                    {testing ? "Đang kiểm tra..." : "Kiểm tra kết nối"}
                    {testSuccess === true ? <Check className="ml-2 h-4 w-4 text-green-500" /> : null}
                    {testSuccess === false ? <AlertTriangle className="ml-2 h-4 w-4 text-destructive" /> : null}
                  </Button>

                  <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                    <Activity className="mt-0.5 h-4 w-4 text-primary" />
                    <p className="text-xs text-primary dark:text-primary">
                      Bài kiểm tra này xác minh nhanh định dạng thông tin xác thực và khả năng sẵn sàng của kết nối trong chế độ cục bộ.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="guardrails" className="m-0 space-y-4">
                <GuardrailItem title="Ẩn dữ liệu nhạy cảm" description="Che bớt dữ liệu nhạy cảm trong log và nội dung gửi đi." />
                <GuardrailItem title="Giới hạn tần suất" description="Giảm rủi ro chi phí tăng đột biến do gọi API quá nhanh." />
                <GuardrailItem title="Hạn mức chi phí" description="Đặt ngưỡng chi tiêu theo tháng cho từng kết nối." />
              </TabsContent>

              <TabsContent value="analytics" className="m-0 space-y-4">
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ANALYTICS_DATA}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis yAxisId="left" dataKey="usage" fontSize={12} />
                      <YAxis yAxisId="right" orientation="right" dataKey="cost" fontSize={12} />
                      <Tooltip />
                      <Area yAxisId="left" type="monotone" dataKey="usage" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.1} />
                      <Area yAxisId="right" type="monotone" dataKey="cost" stroke="#10B981" fill="#10B981" fillOpacity={0.1} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </div>

            <DialogFooter className="border-t bg-muted/20 p-6">
              <Button variant="ghost" onClick={() => onOpenChange(false)} type="button">
                {t("cancel")}
              </Button>
              <Button type="submit" className="min-w-[140px] bg-gradient-primary text-primary-foreground hover:opacity-95" aria-label="Lưu kết nối">
                Lưu kết nối
              </Button>
            </DialogFooter>
          </Tabs>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const GuardrailItem = ({ title, description }: { title: string; description: string }) => (
  <div className="flex items-center justify-between rounded-lg border bg-secondary/30 p-4">
    <div className="space-y-0.5">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <Switch />
  </div>
);
